import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Home, DollarSign, FileCheck } from "lucide-react";
import { Container } from "../../components/layout/Container.jsx";
import { PageHero } from "../../components/shared/PageHero.jsx";
import { SEO } from "../../lib/seo.jsx";

const STEPS = [
  {
    icon: DollarSign,
    title: "1. Know your budget",
    body: "We'll help you understand the all-in number — land + build + stamp duty + connections + landscaping — before you sign anything.",
  },
  {
    icon: Home,
    title: "2. Pick the right design for the block",
    body: "Block size, orientation, and slope all change which designs work. Bring us a block, we'll shortlist designs that fit.",
  },
  {
    icon: FileCheck,
    title: "3. Lock in finance & sign",
    body: "We work with major banks and brokers. Pre-approval first, then full approval after contract signing. No surprises.",
  },
];

const ELIGIBILITY = [
  "First Home Owner Grant (Vic) — eligible new builds under cap",
  "First Home Buyer stamp duty exemption / concession",
  "First Home Loan Deposit Scheme (where applicable)",
  "Regional First Home Buyer top-up grant (eligible regions)",
];

export default function FirstHomeBuyerPage() {
  return (
    <>
      <SEO
        title="First Home Buyer"
        description="A plain-English guide to building your first home — eligibility, grants, and the path from deposit to handover."
      />

      <PageHero
        eyebrow="Get started"
        title="First home buyer guide"
        description="Building a first home is a big call. Here's what we wish every first-home buyer understood before signing."
      />

      <Container className="py-16 lg:py-20">
        <section>
          <h2 className="font-display text-3xl text-text-primary">The path, in three steps</h2>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            {STEPS.map(({ icon: Icon, title, body }) => (
              <article
                key={title}
                className="rounded-2xl border border-border-subtle bg-bg-elevated p-6"
              >
                <div className="grid h-11 w-11 place-items-center rounded-md bg-accent/15 text-accent">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-xl text-text-primary">{title}</h3>
                <p className="mt-2 text-sm text-text-secondary">{body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mt-16 rounded-2xl border border-border-subtle bg-bg-elevated p-8 lg:p-12">
          <p className="eyebrow">Grants &amp; concessions</p>
          <h2 className="mt-3 font-display text-3xl text-text-primary">
            What you may be eligible for
          </h2>
          <p className="mt-3 max-w-2xl text-text-secondary">
            {
              "Government schemes change regularly — we'll always confirm what applies to your build on a call. As a starting point:"
            }
          </p>
          <ul className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-2">
            {ELIGIBILITY.map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm text-text-secondary">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                {item}
              </li>
            ))}
          </ul>
        </section>

        <div className="mt-16 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-border-subtle bg-bg-elevated p-8">
          <div>
            <h3 className="font-display text-2xl text-text-primary">Ready to talk numbers?</h3>
            <p className="mt-1 text-sm text-text-secondary">
              Book a no-pressure first-home-buyer chat with our team.
            </p>
          </div>
          <Link to="/request-an-appointment" className="btn-primary">
            Request an appointment
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Container>
    </>
  );
}
